import { getNinjaConfig } from '@/config/ninja';
import { TIMESTAMP_PROP } from '@/const/general';
import { ninjaBus } from '@/eventbus';
import { getLogger } from '@/logger';
import { getCustomParams } from '@/trackers/params';
import { UserEvent, UserProps } from '@/types/events';
import { HandlerFn } from '@/types/native';
import { getEventMeta, isLaquesisEvent, isNinjaEvent } from '@/utils/event';
import { omit } from '@/utils/object';
import { nativeHandlerFn } from './native/handler';

const logger = getLogger('dataLayer');

export class DataLayer {
  isInitialized = false;

  items: UserEvent[] = [];

  constructor() {
    this.items = getNinjaConfig().dataLayer;
  }

  /**
   * Initialize the dataLayer by adding additional logic to the push method
   */
  init() {
    if (!this.isInitialized) {
      const originalPush = this.items.push;

      this.items.push = (...items: UserEvent[]) => {
        logger.debug('dataLayer.push', ...items);

        // process each item and push it to the dataLayer
        for (const item of items) {
          const pItem = this.processItem(item);
          if (pItem) {
            originalPush.apply(this.items, [pItem]);
          }
        }

        return this.items.length;
      };
      logger.debug('dataLayer initialized');
      this.isInitialized = true;
    }
  }

  processItem(item: UserEvent) {
    logger.debug('Processing item', item);
    const ninjaConfig = getNinjaConfig();
    const eventType = ninjaConfig.specialNames?.find(sn => sn in item);

    if (!eventType) {
      return item;
    }

    if (item.processed) {
      logger.debug('Ninja event already processed:', eventType, item);
      return item;
    }

    const eventValue = Array.isArray(item[eventType]) ? item[eventType].join('_') : (item[eventType] as string);
    const userProps: UserProps = omit(item, ...ninjaConfig.specialNames);

    logger.debug('processing Ninja event:', eventType, eventValue, userProps);

    if (ninjaConfig.isNative) {
      const result = nativeHandlerFn(eventType as HandlerFn, eventValue, userProps);
      logger.debug('nativeHandlerFn result:', result);
      return null;
    }

    if (eventType === 'cleanup') {
      logger.debug('cleanup');
      this.cleanup();
      if (typeof item.cleanup === 'function') {
        logger.debug('cleanup function called');
        item.cleanup();
      }
      return undefined;
    }

    const isLQEvent = isLaquesisEvent(eventValue);
    const customProps = getCustomParams(userProps, this.items, getNinjaConfig().disablePropertyPropagation || isLQEvent);
    const eventMeta = getEventMeta();
    const emittedEvent = ninjaBus.emit(eventType, eventValue, customProps, eventMeta);

    if (isLQEvent) {
      return null;
    }

    return {
      ...item,
      [TIMESTAMP_PROP]: emittedEvent?.t ?? Date.now(),
      processed: true,
    };
  }

  /**
   * Process any existing items in the dataLayer Called right after the dataLayer is initialized
   */
  async processDataLayer() {
    if (this.items.length) {
      logger.debug('processing existing items in dataLayer', this.items);

      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        const processed = this.processItem(item);

        if (processed) {
          this.items[i] = processed;
        }
      }
    }
  }

  push(item: UserEvent) {
    return this.items.push(item);
  }

  /**
   * Cleanup the dataLayer and keep the push() logic.
   * `window.dataLayer` should retain all non-ninja events
   */
  cleanup() {
    let continueRunning = true;

    try {
      while (continueRunning) {
        continueRunning = false;
        for (let i = 0; i < this.items.length; i++) {
          if (isNinjaEvent(this.items[i])) {
            continueRunning = undefined === this.items[i].cleanup;
            this.items.splice(i, 1);
            i--;
          }
        }
      }
    } catch (error) {
      logger.info(error);
    }
  }
}

export const dataLayer = new DataLayer();
